<template>
  <div class="flex flex-col gap-2">
    <div>{{ targetPrompt }}</div>
    <n-input size="small" :value="targetText" disabled />

    <div>{{ relatedItemsPrompt }}</div>
    <PuppyInputSelect
      :items="relatedSelectItems"
      :active="false"
      :active-index="-1"
      :selected-indices="[]"
      :visible-rows="4"
      :context-menu-enabled="false"
      empty-text=""
      item-key-field="id"
      item-label-field="label"
      selection-behavior="single"
    />
  </div>
</template>

<script setup lang="ts">
import {computed} from 'vue';
import {NInput} from 'naive-ui';
import PuppyInputSelect from '@/components/PuppyInputSelect/PuppyInputSelect.vue';

/**
 * 可删除目标类型。
 */
type FeatureTreeDeleteConfirmItemKind =
  | 'feature'
  | 'sketch'
  | 'mate'
  | 'linear-component-pattern'
  | 'part'
  | 'assembly';

/**
 * 可删除项摘要。
 */
interface FeatureTreeDeleteConfirmItem {
  /** 稳定 ID。 */
  readonly id: string;
  /** 设计树显示名称，不包含类型后缀。 */
  readonly label: string;
  /** 项目类型。 */
  readonly kind: FeatureTreeDeleteConfirmItemKind;
}

interface FeatureTreeDeleteConfirmDialogProps {
  /** 待删除目标。 */
  readonly target: FeatureTreeDeleteConfirmItem;
  /** 会被一起删除的相关项目。 */
  readonly relatedItems?: readonly FeatureTreeDeleteConfirmItem[];
  /** 目标项上方的操作提示。 */
  readonly targetPrompt?: string;
  /** 从属项列表上方的影响提示。 */
  readonly relatedItemsPrompt?: string;
}

const props = withDefaults(defineProps<FeatureTreeDeleteConfirmDialogProps>(), {
  relatedItems: () => [],
  targetPrompt: '删除以下项目？',
  relatedItemsPrompt: '同时删除以下从属项目：',
});

/**
 * 解析项目类型显示名称。
 *
 * @param kind 项目类型。
 * @returns SolidWorks 风格的类型后缀文本。
 */
function resolveKindLabel(kind: FeatureTreeDeleteConfirmItemKind): string {
  switch (kind) {
    case 'feature':
      return '特征';
    case 'sketch':
      return '草图';
    case 'mate':
      return '配合';
    case 'linear-component-pattern':
      return '线性零部件阵列';
    case 'assembly':
      return '子装配体';
    case 'part':
      return '零部件';
    default:
      return '项目';
  }
}

/**
 * 格式化确认框中的项目显示文本。
 *
 * @param item 项目摘要。
 * @returns 带类型后缀的显示文本。
 */
function formatItemText(item: FeatureTreeDeleteConfirmItem): string {
  return `${item.label}(${resolveKindLabel(item.kind)})`;
}

const targetText = computed(() => formatItemText(props.target));
const relatedSelectItems = computed(() => props.relatedItems.map((item) => ({
  id: item.id,
  label: formatItemText(item),
})));
</script>

<style scoped>
</style>
